import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2, Shield, Eye, EyeOff, CheckCircle2, XCircle, ShieldCheck, AlertCircle,
  Play, Database, List, ChevronDown, ChevronUp, Clock, Pencil,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { SubscriptionDialog } from '@/components/ui/subscription-dialog';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { useAuthStore } from '@/stores/auth';
import { fsopApi, type FsopCredential, type SyncTask, type SyncStatus, type DownloadLog } from '@/services/api';
import { parseApiError } from '@/services/api';

const STATUS_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  pending: { icon: Clock, label: '待处理', color: 'text-gray-500', bg: 'bg-gray-50' },
  running: { icon: Loader2, label: '运行中', color: 'text-blue-600', bg: 'bg-blue-50' },
  list_completed: { icon: List, label: '列表完成', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  completed: { icon: CheckCircle2, label: '已完成', color: 'text-green-600', bg: 'bg-green-50' },
  cancelled: { icon: XCircle, label: '已取消', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  failed: { icon: AlertCircle, label: '失败', color: 'text-red-600', bg: 'bg-red-50' },
};

function fmtDate(s: string | null) {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' });
}

function ProgressBar({ pct, label, sub }: { pct: number; label: string; sub?: string }) {
  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{pct}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div className="bg-blue-500 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export function FsopSyncSection() {
  const { toast } = useToast();
  const { token } = useAuthStore();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Credentials
  const [credential, setCredential] = useState<FsopCredential | null>(null);
  const [credLoading, setCredLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLicense, setShowLicense] = useState(false);
  const [showMessid, setShowMessid] = useState(false);
  const [checking, setChecking] = useState(false);
  const [editingCredential, setEditingCredential] = useState(false);
  const [archiveNo, setArchiveNo] = useState('');
  const [licenseNum, setLicenseNum] = useState('');
  const [phone, setPhone] = useState('');
  const [messidMask, setMessidMask] = useState('');
  const [newMessid, setNewMessid] = useState('');

  // Sync
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [tasks, setTasks] = useState<SyncTask[]>([]);
  const [logs, setLogs] = useState<DownloadLog[]>([]);
  const [activeTask, setActiveTask] = useState<SyncTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [tasksExpanded, setTasksExpanded] = useState(true);
  const { showDialog, setShowDialog } = useSubscriptionGuard();

  // Fetch credential
  const fetchCredential = useCallback(async () => {
    if (!token) return;
    setCredLoading(true);
    try {
      const res = await fsopApi.getMyCredential();
      const payload = res.data;
      if (payload.success && payload.data) {
        const cred = payload.data as FsopCredential;
        setCredential(cred);
        setArchiveNo(cred.archiveNo ?? '');
        setLicenseNum(cred.licenseNum ?? '');
        setPhone(cred.phone ?? '');
        setMessidMask(cred.messidMask ?? '');
        setNewMessid('');
        setEditingCredential(false);
      } else {
        setCredential(null);
        setEditingCredential(true);
      }
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowDialog(true);
      }
      setCredential(null);
    } finally {
      setCredLoading(false);
    }
  }, [token]);

  // Fetch sync data
  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fsopApi.getSyncStatus();
      const d = (res.data as any)?.data ?? res.data;
      setSyncStatus(d as SyncStatus);
      if ((d as SyncStatus).isSyncing && (d as SyncStatus).lastSyncTask) {
        setActiveTask((d as SyncStatus).lastSyncTask as SyncTask);
      }
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowDialog(true);
      }
    }
  }, [token]);

  const fetchTasks = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fsopApi.getSyncTasks({ pageSize: 20 });
      setTasks((r.data?.results ?? []) as SyncTask[]);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowDialog(true);
      }
    }
  }, [token]);

  const fetchLogsFn = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fsopApi.getSyncLogs();
      setLogs((res.data?.data ?? res.data ?? []) as DownloadLog[]);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowDialog(true);
      }
    }
  }, [token]);

  const fetchProgress = useCallback(async (taskId: string) => {
    if (!token) return null;
    try {
      const r = await fsopApi.getSyncProgress(taskId);
      return (r.data as any)?.data ?? r.data as SyncTask;
    } catch { return null; }
  }, [token]);

  const init = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStatus(), fetchTasks(), fetchLogsFn()]);
    setLoading(false);
  }, [fetchStatus, fetchTasks, fetchLogsFn]);

  useEffect(() => { fetchCredential(); }, [fetchCredential]);
  useEffect(() => { init(); }, [init]);

  // Polling — 使用 setTimeout 递归替代 setInterval，增加超时保护
  const MAX_POLL_DURATION = 10 * 60 * 1000; // 10 分钟

  useEffect(() => {
    if (!activeTask?.id || !syncStatus?.isSyncing) return;

    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    const startTime = Date.now();

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startTime > MAX_POLL_DURATION) {
        fetchStatus();
        fetchTasks();
        return;
      }

      const prog = await fetchProgress(activeTask.id);
      if (cancelled) return;

      if (prog) {
        setActiveTask(prog);
        if (prog.status !== 'running' && prog.status !== 'pending') {
          fetchStatus();
          fetchTasks();
          return;
        }
      }

      timer = setTimeout(poll, 2000);
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeTask?.id, syncStatus?.isSyncing]);

  // Credential handlers
  const handleCredSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!archiveNo.trim() && !licenseNum.trim()) {
      toast({ title: '请填写档案号或执照号', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload: { archiveNo: string; licenseNum: string; phone: string; messid?: string } = {
        archiveNo,
        licenseNum,
        phone,
      };
      if (newMessid.trim()) {
        payload.messid = newMessid.trim();
      }
      if (credential?.id) {
        await fsopApi.updateCredential(credential.id, payload);
        toast({ title: '凭据更新成功' });
      } else {
        await fsopApi.createCredential(payload);
        toast({ title: '凭据保存成功' });
      }
      setNewMessid('');
      fetchCredential();
      fetchStatus();
    } catch (err: any) {
      if (err?.response?.status === 402) {
        setShowDialog(true);
      } else {
        const { message } = parseApiError(err);
        toast({ title: message, variant: 'destructive' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCredDelete = async () => {
    if (!credential?.id) return;
    if (!confirm('确定要删除 FSOP 凭据吗？删除后将无法自动同步飞行记录。')) return;
    setSaving(true);
    try {
      await fsopApi.deleteCredential(credential.id);
      setCredential(null);
      setArchiveNo('');
      setLicenseNum('');
      setPhone('');
      setMessidMask('');
      setNewMessid('');
      setEditingCredential(true);
      toast({ title: '凭据已删除' });
      fetchStatus();
    } catch (err: any) {
      if (err?.response?.status === 402) {
        setShowDialog(true);
      } else {
        const { message } = parseApiError(err);
        toast({ title: message, variant: 'destructive' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCredCheck = async () => {
    if (!credential?.id) return;
    setChecking(true);
    try {
      const res = await fsopApi.checkCredential();
      const payload = res.data;
      if (payload.success) {
        toast({
          title: payload.message || '认证检查完成',
          variant: payload.data?.isValid ? 'default' : 'destructive',
        });
        fetchCredential();
      } else {
        toast({ title: payload.message || '检查失败', variant: 'destructive' });
      }
    } catch (err: any) {
      if (err?.response?.status === 402) {
        setShowDialog(true);
      } else {
        const { message } = parseApiError(err);
        toast({ title: message, variant: 'destructive' });
      }
    } finally {
      setChecking(false);
    }
  };

  // Sync handlers
  const handleStart = async () => {
    setActionLoading(true);
    try {
      await fsopApi.startSync();
      toast({ title: '同步任务已启动' });
      await fetchStatus();
    } catch (e: any) {
      if (e?.response?.status === 402) {
        setShowDialog(true);
      } else {
        const { message } = parseApiError(e);
        toast({ title: message, variant: 'destructive' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!activeTask) return;
    setActionLoading(true);
    try {
      await fsopApi.cancelSync(activeTask.id);
      toast({ title: '同步任务已取消' });
      if (pollingRef.current) clearInterval(pollingRef.current);
      fetchStatus();
      fetchTasks();
    } catch (e: any) {
      if (e?.response?.status === 402) {
        setShowDialog(true);
      } else {
        const { message } = parseApiError(e);
        toast({ title: message, variant: 'destructive' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (credLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const hasCredential = !!credential?.id;
  const overallPct = activeTask?.progress?.overallPercentage ?? 0;

  return (
    <div className="space-y-6">
      <SubscriptionDialog isOpen={showDialog} onClose={() => setShowDialog(false)} />
      {/* Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            FSOP 凭据
          </CardTitle>
          <CardDescription>管理 FSOP 系统的登录凭证</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center gap-2 mb-4 px-4 py-3 rounded-lg border ${
            hasCredential
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            {hasCredential ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">已配置 FSOP 凭据</span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5" />
                <span className="font-medium">尚未配置 FSOP 凭据，请先添加</span>
              </>
            )}
          </div>

          {hasCredential && credential?.checkedAt != null && (
            <div className={`flex items-center gap-2 mb-4 px-4 py-3 rounded-lg border ${
              credential.isValid === true
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : credential.isValid === false
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-gray-50 border-gray-200 text-gray-600'
            }`}>
              {credential.isValid === true ? (
                <ShieldCheck className="w-5 h-5" />
              ) : credential.isValid === false ? (
                <AlertCircle className="w-5 h-5" />
              ) : (
                <Shield className="w-5 h-5" />
              )}
              <span className="font-medium">
                {credential.isValid === true
                  ? '凭据认证通过'
                  : credential.isValid === false
                    ? '凭据认证失败'
                    : '凭据未验证'}
              </span>
              <span className="text-sm opacity-80 ml-auto">
                上次检查: {new Date(credential.checkedAt).toLocaleString('zh-CN')}
              </span>
            </div>
          )}

          {!editingCredential && hasCredential ? (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 text-sm">
                  <p className="text-green-800">
                    <span className="font-medium">档案号：</span>
                    {credential.archiveNo || '-'}
                  </p>
                  <p className="text-green-800">
                    <span className="font-medium">登录令牌：</span>
                    {credential.messidMask || '未保存'}
                  </p>
                  <p className="text-green-800">
                    <span className="font-medium">绑定手机：</span>
                    {credential.phone || '-'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditingCredential(true)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  修改登录信息
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCredSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fsop-archive">档案号</Label>
                  <Input
                    id="fsop-archive"
                    value={archiveNo}
                    onChange={(e) => setArchiveNo(e.target.value)}
                    placeholder="如: 00000001"
                    disabled={saving}
                  />
                  <p className="text-xs text-gray-400">FSOP 档案号，通常为8位数字</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fsop-license">执照号（身份证号）</Label>
                  <div className="relative">
                    <Input
                      id="fsop-license"
                      type={showLicense ? 'text' : 'password'}
                      value={licenseNum}
                      onChange={(e) => setLicenseNum(e.target.value)}
                      placeholder="身份证号，用于 FSOP 登录"
                      disabled={saving}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLicense((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showLicense ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">FSOP 系统使用身份证号登录</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fsop-phone">绑定手机号</Label>
                  <Input
                    id="fsop-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="用于接收 FSOP 通知"
                    disabled={saving}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fsop-messid">登录令牌 (messid)</Label>
                  {messidMask ? (
                    <p className="text-xs text-gray-500">
                      当前已保存：{messidMask}
                    </p>
                  ) : hasCredential ? (
                    <p className="text-xs text-amber-600">
                      检测到已保存凭据，但登录令牌无法读取，可能是加密密钥已轮换，请重新输入 messid 后保存。
                    </p>
                  ) : null}
                  <div className="relative">
                    <Input
                      id="fsop-messid"
                      type={showMessid ? 'text' : 'password'}
                      value={newMessid}
                      onChange={(e) => setNewMessid(e.target.value)}
                      placeholder={messidMask ? '留空则保持不变，输入则更新' : '请输入 FSOP 登录后的 messid'}
                      disabled={saving}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowMessid((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showMessid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                {hasCredential && (
                  <Button type="button" variant="ghost" onClick={() => setEditingCredential(false)}>
                    取消
                  </Button>
                )}
                <Button type="submit" disabled={saving || checking}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {hasCredential ? '保存修改' : '保存凭据'}
                </Button>
                {hasCredential && (
                  <>
                    <Button type="button" variant="outline" onClick={handleCredCheck} disabled={saving || checking}>
                      {checking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      认证检查
                    </Button>
                    <Button type="button" variant="destructive" onClick={handleCredDelete} disabled={saving || checking}>
                      删除凭据
                    </Button>
                  </>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Database, label: '记录总数', value: syncStatus?.totalRecords ?? 0, color: 'text-gray-600' },
          { icon: CheckCircle2, label: '已同步详情', value: syncStatus?.hasDetailCount ?? 0, color: 'text-green-600' },
          { icon: Clock, label: '待同步详情', value: syncStatus?.pendingDetailCount ?? 0, color: 'text-amber-600' },
          { icon: AlertCircle, label: '凭据状态', value: syncStatus?.hasCredential ? '已配置' : '未配置', color: syncStatus?.hasCredential ? 'text-green-600' : 'text-red-500' },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label}>
            <CardContent className="pt-4">
              <div className={`flex items-center gap-2 text-sm mb-1 ${color}`}>
                <Icon className="w-4 h-4" /> {label}
              </div>
              <div className="text-2xl font-bold">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sync Control */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>同步控制</CardTitle>
              <CardDescription>
                {syncStatus?.isSyncing
                  ? '同步任务正在进行中，请勿关闭页面'
                  : '点击按钮开始从 FSOP 同步飞行记录'}
              </CardDescription>
            </div>
            {activeTask && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_CONFIG[activeTask.status]?.bg} ${STATUS_CONFIG[activeTask.status]?.color}`}>
                {activeTask.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  activeTask.status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> :
                  activeTask.status === 'failed' ? <AlertCircle className="w-4 h-4" /> :
                  activeTask.status === 'cancelled' ? <XCircle className="w-4 h-4" /> : null}
                {STATUS_CONFIG[activeTask.status]?.label ?? activeTask.status}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(syncStatus?.isSyncing || activeTask?.status === 'running') && activeTask && (
            <div className="mb-5 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-blue-700 font-medium">总进度</span>
                <span className="text-blue-900 font-bold">{overallPct}%</span>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-3 mb-3">
                <div className="bg-blue-600 h-3 rounded-full transition-all" style={{ width: `${overallPct}%` }} />
              </div>
              {activeTask.stage === 'downloading_list' && (
                <ProgressBar
                  pct={activeTask?.progress?.listPercentage ?? 0}
                  label="列表同步"
                  sub={`第 ${activeTask.progress?.list?.currentPage} 页，已处理 ${activeTask.progress?.list?.newRecords} 条`}
                />
              )}
              {activeTask.stage === 'downloading_records' && (
                <>
                  <div className="text-xs text-blue-600 mb-2">
                    详情同步: {activeTask.processedRecords}/{activeTask.totalDetailRecords}
                    {activeTask.successCount > 0 && <span className="ml-2 text-green-600">✓ {activeTask.successCount}</span>}
                    {activeTask.failedCount > 0 && <span className="ml-2 text-red-600">✗ {activeTask.failedCount}</span>}
                  </div>
                  <ProgressBar
                    pct={activeTask?.progress?.detailPercentage ?? 0}
                    label="详情下载"
                    sub={`${activeTask.processedRecords}/${activeTask.totalDetailRecords} 条`}
                  />
                </>
              )}
              {activeTask.message && <p className="text-sm text-blue-600 mt-2">{activeTask.message}</p>}
            </div>
          )}
          <div className="flex gap-3">
            {!syncStatus?.hasCredential ? (
              <p className="text-sm text-gray-500">请先配置 FSOP 凭据后再同步</p>
            ) : syncStatus?.isSyncing ? (
              <Button onClick={handleCancel} variant="destructive" disabled={actionLoading}>
                {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <XCircle className="mr-2 h-4 w-4" />
                取消同步
              </Button>
            ) : (
              <Button onClick={handleStart} disabled={actionLoading}>
                {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Play className="mr-2 h-4 w-4" />
                开始同步
              </Button>
            )}
          </div>
          {(activeTask?.errorMessage || syncStatus?.lastSyncTask?.errorMessage) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <strong>错误:</strong> {activeTask?.errorMessage || syncStatus?.lastSyncTask?.errorMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task History */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => setTasksExpanded((v) => !v)}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">同步历史</CardTitle>
              <CardDescription>最近的同步任务记录</CardDescription>
            </div>
            {tasksExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </CardHeader>
        {tasksExpanded && (
          <CardContent>
            {tasks.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">暂无同步记录</p>
            ) : (
              <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
                {tasks.map((task) => {
                  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
                  const TaskIcon = cfg.icon;
                  return (
                    <div key={task.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                        <TaskIcon className={`w-4 h-4 ${cfg.color} ${task.status === 'running' ? 'animate-spin' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{cfg.label}</span>
                          {task.progress?.overallPercentage > 0 && <span className="text-xs text-gray-400">({task.progress.overallPercentage}%)</span>}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {fmtDate(task.createdAt)}
                          {task.startedAt && ` → 开始: ${fmtDate(task.startedAt)}`}
                          {task.completedAt && ` → 完成: ${fmtDate(task.completedAt)}`}
                        </div>
                        {task.message && <div className="text-xs text-gray-500 mt-0.5 truncate">{task.message}</div>}
                        {task.errorMessage && <div className="text-xs text-red-500 mt-0.5">{task.errorMessage}</div>}
                      </div>
                      <div className="text-right text-xs text-gray-400 flex-shrink-0">
                        {task.newListRecords > 0 && <div>新增 {task.newListRecords} 条</div>}
                        {task.successCount > 0 && <div className="text-green-600">成功 {task.successCount}</div>}
                        {task.failedCount > 0 && <div className="text-red-500">失败 {task.failedCount}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Download Logs */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => setLogsExpanded((v) => !v)}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">下载日志</CardTitle>
              <CardDescription>同步过程的详细日志</CardDescription>
            </div>
            {logsExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </CardHeader>
        {logsExpanded && (
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">暂无日志</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {logs.map((log) => {
                  const lc: Record<string, string> = {
                    info: 'text-blue-600', warning: 'text-amber-600', error: 'text-red-600',
                    debug: 'text-gray-400', critical: 'text-red-800 font-bold',
                  };
                  return (
                    <div key={log.id} className="flex gap-3 text-sm py-1 border-b border-gray-50 last:border-0">
                      <span className={`flex-shrink-0 font-mono text-xs w-12 ${lc[log.level] || 'text-gray-400'}`}>
                        [{log.level?.toUpperCase()}]
                      </span>
                      <span className="text-gray-500 flex-shrink-0 text-xs w-20">{fmtDate(log.createdAt)}</span>
                      {log.stage && <span className="text-gray-400 flex-shrink-0 text-xs">[{log.stage}]</span>}
                      <span className="text-gray-700">{log.message}</span>
                      {log.exceptionMessage && <span className="text-red-500 text-xs">({log.exceptionMessage})</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
